import { z } from 'zod';

import { RehireConditionTag, RehireRecommendation, ReplacementValue, ReplacementValueBasisTag } from '@/common/graphql';

export const directReportChangeSchema = z.object({
  directReportEmployeeId: z.string(),
  changePrimaryManagerId: z.boolean().default(false),
  changeDottedLineManagerId: z.boolean().default(false),
  newPrimaryManagerId: z.string().nullable().default(null),
  newDottedLineManagerId: z.string().nullable().default(null),
});

export const ReplacementValueLabels: Record<ReplacementValue, string> = {
  [ReplacementValue.Low]: 'Can find replacement in the market',
  [ReplacementValue.Medium]: 'Requires training time, but replaceable through external hiring',
  [ReplacementValue.High]:
    'Possesses critical expertise AND exceptional AnyMind Essentials (Grit / Problem Solving) that are difficult to source or develop',
  [ReplacementValue.Extreme]: 'Rare talent across the entire AnyMind Group or an extraordinary business',
};

export const ReplacementValueBasisTagLabels: Record<ReplacementValueBasisTag, string> = {
  [ReplacementValueBasisTag.MarketAvailable]: 'Sufficient supply available in the market',
  [ReplacementValueBasisTag.TrainableExternal]: 'Replaceable through external hire + training',
  [ReplacementValueBasisTag.DomainExpertise]: 'Rare domain / industry knowledge',
  [ReplacementValueBasisTag.TechnicalExpertise]: 'Rare technical skills',
  [ReplacementValueBasisTag.ClientRelationship]: 'Key client relationships tied to this person',
  [ReplacementValueBasisTag.CultureLeadership]: 'Exemplifies AnyMind Essentials (Grit / Problem Solving)',
  [ReplacementValueBasisTag.CrossRegionalImpact]: 'Impact spans multiple markets or regions',
  [ReplacementValueBasisTag.BusinessContinuityRisk]: 'Departure creates operational continuity risk',
};

export const RehireRecommendationLabels: Record<RehireRecommendation, string> = {
  [RehireRecommendation.StronglyRecommended]: 'Strongly Recommended',
  [RehireRecommendation.Conditional]: 'Conditional: Eligible with conditions',
  [RehireRecommendation.NotRecommended]: 'Not Recommended',
};

export const RehireConditionTagLabels: Record<RehireConditionTag, string> = {
  [RehireConditionTag.DifferentTeam]: 'Re-hire in a different team / department',
  [RehireConditionTag.DifferentRole]: 'Re-hire at a different seniority or role',
  [RehireConditionTag.RequiresApproval]: 'Requires additional approval at time of re-hire',
  [RehireConditionTag.PerformanceConditions]: 'Subject to performance conditions',
};

export const EmployeeOffBoarding = z.object({
  employeeId: z.string().default(''),
  detailReasonForLeaving: z.string().nullable().default(''),
  employmentEndDate: z.string().nullable().default(''),
  lastWorkingDate: z.string().nullable().default(''),
  reasonForLeavingId: z.string().nullable().default(null),
  deactivateGreenhouse: z.boolean().default(false),
  suspendGoogleAccount: z.boolean().default(false),
  suspendEloomiAccount: z.boolean().default(false),
  directReportManagerChangeReservations: z.array(directReportChangeSchema).optional(),
  replacementValue: z.nativeEnum(ReplacementValue).nullable().default(null),
  replacementValueBasis: z.array(z.nativeEnum(ReplacementValueBasisTag)).default([]),
  rehireRecommendation: z.nativeEnum(RehireRecommendation).nullable().default(null),
  rehireConditions: z.array(z.nativeEnum(RehireConditionTag)).default([]),
  rehireConditionNote: z.string().nullable().default(''),
  // Performance Review period key (`2026-Q1_Q2`) from which the employee is left out of tracking; null means none.
  performanceReviewExclusion: z.string().nullable().default(null),
  // True once HR picked a value in the select. Until then nothing is sent, so the server keeps the stored period
  // and moves it with a changed Last working date.
  performanceReviewExclusionChanged: z.boolean().default(false),
});
export type EmployeeOffBoardingType = z.infer<typeof EmployeeOffBoarding>;
export type DirectReportChangeType = z.infer<typeof directReportChangeSchema>;
